import React, { useState, useEffect } from 'react';

import {
  SaveOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import CurrencySelect from '@components/shared/selects/CurrencySelect';
import { STORAGE_BILLING_MODE_OPTIONS } from '@components/shared/rate_cards/storageBillingModes';
import PageHeader from '@shared/PageHeader';
import { useOperatorAuth } from '@contexts/operator/OperatorAuthContext';
import { merchantService } from '@services/operator/merchantService';
import { operatorApiFetch } from '@services/operator/apiService';
import {
  Alert,
  Card,
  Form,
  Input,
  Button,
  Select,
  Space,
  message,
  Row,
  Col,
  Tooltip,
  Typography
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const { Option } = Select;
const { Title } = Typography;

const MerchantNew = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const billingMode = Form.useWatch('storage_billing_mode', form);
  const { operator } = useOperatorAuth();

  useEffect(() => {
    fetchWarehouses();
    fetchOrganizationSettings();
  }, []);

  const fetchWarehouses = async () => {
    try {
      const response = await operatorApiFetch('warehouses?page_size=100');
      const data = await response.json();
      if (data.success) {
        setWarehouses(data.data);
        // 如果只有一個倉庫，自動選為預設
        if (data.data.length === 1) {
          form.setFieldValue('default_warehouse_id', data.data[0].id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch warehouses:', error);
    }
  };

  const fetchOrganizationSettings = async () => {
    try {
      const response = await operatorApiFetch('organizations/settings');
      const data = await response.json();
      if (data.success && data.data?.default_currency) {
        form.setFieldValue('default_currency', data.data.default_currency);
      }
    } catch (error) {
      console.error('Failed to fetch organization settings:', error);
    }
  };

  const onFinish = async (values) => {
    setSubmitting(true);
    try {
      // Add current operator's warehouse as default if available (though backend handles assignment if needed)
      // Actually, the API create implementation allows the operator to create it.
      // We will let the user configure warehouses later or rely on defaults.

      const response = await merchantService.create(values);
      if (response.success) {
        message.success(t('common.message.create_success'));
        // If created from onboarding, the OnboardingDrawer will detect the route change/success
        // 導向該筆記錄的 show 頁；replace 避免 New 頁殘留在 history
        navigate(`/merchants/${response.data?.id}`, { replace: true });
      } else {
        message.error(response.message || t('common.error.create_failed'));
      }
    } catch (error) {
      console.error('Create merchant error:', error);
      message.error(t('common.error.create_failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <PageHeader items={['merchant_settings', 'merchant_settings.merchants', { label: t('operator.menu.merchants.new') }]} />
      <Card bordered={false} className="shadow-sm">
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            inventory_traceability_mode: 'aggregated',
            storage_billing_mode: 'by_sku_pallet'
          }}
        >
          {/* 基本資訊 */}
          <Title level={5} style={{ marginBottom: 16 }}>
            {t('operator.merchants.show.tabs.info')}
          </Title>
          {/* Row 1: 貨主名稱 | (狀態開關位 — New 頁不顯示，留白以對齊 Show 編輯版面) */}
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="name"
                label={t('operator.merchants.show.fields.name')}
                rules={[{ required: true, message: t('operator.merchants.show.validation.name_required') }]}
              >
                <Input placeholder={t('operator.merchants.show.placeholders.name')} />
              </Form.Item>
            </Col>
            <Col span={12} />
          </Row>

          {/* Row 2: 預設幣別 | 電話 */}
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="default_currency"
                label={t('operator.merchants.show.fields.default_currency')}
                rules={[{ required: true, message: t('common.validation.required') }]}
              >
                <CurrencySelect apiFetch={operatorApiFetch} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="phone"
                label={t('operator.merchants.show.fields.phone')}
              >
                <Input placeholder={t('operator.merchants.show.placeholders.phone')} />
              </Form.Item>
            </Col>
          </Row>

          {/* Row 3: 州/省 | 城市 | 區域 */}
          <Row gutter={24}>
            <Col span={8}>
              <Form.Item name="state" label={t('models.merchant.state')}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="city"
                label={t('operator.merchants.show.fields.city')}
              >
                <Input placeholder={t('operator.merchants.show.placeholders.city')} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="district" label={t('models.merchant.district')}>
                <Input />
              </Form.Item>
            </Col>
          </Row>

          {/* Row 4: 郵遞區號 | 地址 */}
          <Row gutter={24}>
            <Col span={8}>
              <Form.Item
                name="postal_code"
                label={t('operator.merchants.show.fields.postal_code')}
              >
                <Input placeholder={t('operator.merchants.show.placeholders.postal_code')} />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item
                name="address"
                label={t('operator.merchants.show.fields.address')}
              >
                <Input placeholder={t('operator.merchants.show.placeholders.address')} />
              </Form.Item>
            </Col>
          </Row>

          {/* 計費設定 */}
          <Title level={5} style={{ marginTop: 8, marginBottom: 16 }}>
            {t('operator.merchants.show.fields.settings')}
          </Title>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="inventory_traceability_mode"
                label={
                  <Space>
                    {t('operator.merchants.show.fields.inventory_traceability_mode')}
                    <Tooltip title={t('operator.merchants.show.tooltips.inventory_traceability_mode')} overlayStyle={{ whiteSpace: 'pre-line', maxWidth: 400 }}>
                      <InfoCircleOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />
                    </Tooltip>
                  </Space>
                }
              >
                <Select>
                  <Option value="aggregated">{t('operator.merchants.show.traceability_modes.aggregated')}</Option>
                  <Option value="isolated">{t('operator.merchants.show.traceability_modes.isolated')}</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="storage_billing_mode"
                label={
                  <Space>
                    {t('operator.merchants.show.fields.storage_billing_mode')}
                    <Tooltip title={t('operator.merchants.show.tooltips.storage_billing_mode')} overlayStyle={{ whiteSpace: 'pre-line', maxWidth: 400 }}>
                      <InfoCircleOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />
                    </Tooltip>
                  </Space>
                }
              >
                <Select>
                  {STORAGE_BILLING_MODE_OPTIONS.map(mode => (
                    <Option key={mode} value={mode}>
                      {t(`operator.merchants.show.billing_modes.${mode}`)}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            {billingMode && (
              <Col span={24}>
                <div style={{ marginBottom: '24px', marginTop: '-12px' }}>
                  <Alert
                    type="info"
                    showIcon
                    message={
                      <Space direction="vertical" size={0}>
                        <div>{t(`operator.merchants.show.billing_mode_details.${billingMode}.desc`)}</div>
                        <div style={{ fontSize: '12px', color: 'rgba(0,0,0,0.65)' }}>
                          <strong>{t('operator.merchants.show.fields.required_field')}: </strong>
                          {t(`operator.merchants.show.billing_mode_details.${billingMode}.required`)}
                        </div>
                        <div style={{ fontSize: '12px', color: 'rgba(0,0,0,0.65)', background: 'rgba(0,0,0,0.02)', padding: '4px 8px', borderRadius: '4px', marginTop: '4px' }}>
                          {t(`operator.merchants.show.billing_mode_details.${billingMode}.example`)}
                        </div>
                      </Space>
                    }
                  />
                </div>
              </Col>
            )}

            <Col span={12}>
              <Form.Item
                name="default_warehouse_id"
                label={t('operator.merchants.show.fields.default_warehouse')}
                extra={t('operator.merchants.show.hints.default_warehouse')}
              >
                <Select
                  placeholder={t('operator.merchants.show.placeholders.select_default_warehouse')}
                  allowClear
                >
                  {warehouses.map(wh => (
                    <Option key={wh.id} value={wh.id}>{wh.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginTop: 24, marginBottom: 0 }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => navigate('/merchants')}>
                {t('common.button.cancel')}
              </Button>
              <Button type="primary" htmlType="submit" loading={submitting} icon={<SaveOutlined />}>
                {t('common.button.create')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default MerchantNew;
